// useInvoice.js - Custom hook for invoice state and handlers.
// Updated: Added billFromDocumentId and billToDocumentId state fields for Document ID support.
// Updated: Integrated isLoadedInvoice from context; calls createNewInvoice() for new invoices to centralize logic.
// Fixed: For new invoices, explicitly set invoiceNumber to the next global number to ensure the field is populated.
// Fixed: Removed unused 'isLoadedInvoice' from destructuring to eliminate ESLint warning.
// Removed manual invoice number calculation; now handled by context via createNewInvoice/loadInvoice.

import { useState, useEffect } from 'react';
import { useGlobalContext } from '../hooks/useGlobalContext';

const useInvoice = () => {
  // Fixed: Removed unused 'isLoadedInvoice' from destructuring
  const { lastInvoiceNumber, currentInvoice, createNewInvoice } = useGlobalContext();

  // State variables (extracted from InvoiceForm)
  const [isOpen, setIsOpen] = useState(false);
  const [currency, setCurrency] = useState('$');
  const [dateOfIssue, setDateOfIssue] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [billTo, setBillTo] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [billToDocumentId, setBillToDocumentId] = useState('');  // NEW: Document ID for Bill To
  const [billFrom, setBillFrom] = useState('');
  const [billFromEmail, setBillFromEmail] = useState('');
  const [billFromAddress, setBillFromAddress] = useState('');
  const [billFromDocumentId, setBillFromDocumentId] = useState('');  // NEW: Document ID for Bill From
  const [notes, setNotes] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [discountRate, setDiscountRate] = useState('');
  const [items, setItems] = useState([
    { id: "0", description: '', price: '0.00', quantity: 1 }
  ]);
  const [logo, setLogo] = useState('');  // Base64 string for uploaded logo

  // Updated: Initialize invoiceNumber to '' (set via context in useEffect)
  const [invoiceNumber, setInvoiceNumber] = useState('');

  // Updated: useEffect to populate state from currentInvoice when loaded, or reset for new invoices via context.
  // Calls createNewInvoice() for new invoices to ensure proper numbering and flag management.
  // Fixed: For new invoices, compute and set the next invoiceNumber locally to populate the field.
  // NEW: Added Document ID fields to population logic
  useEffect(() => {
    if (currentInvoice) {
      // Load existing invoice data
      setInvoiceNumber(currentInvoice.invoiceNumber || '1');
      setCurrency(currentInvoice.currency || '$');
      setDateOfIssue(currentInvoice.dateOfIssue || '');
      setInvoiceDate(currentInvoice.invoiceDate || '');
      setBillTo(currentInvoice.billTo || '');
      setBillToEmail(currentInvoice.billToEmail || '');
      setBillToAddress(currentInvoice.billToAddress || '');
      setBillToDocumentId(currentInvoice.billToDocumentId || '');  // NEW
      setBillFrom(currentInvoice.billFrom || '');
      setBillFromEmail(currentInvoice.billFromEmail || '');
      setBillFromAddress(currentInvoice.billFromAddress || '');
      setBillFromDocumentId(currentInvoice.billFromDocumentId || '');  // NEW
      setNotes(currentInvoice.notes || '');
      setTermsAndConditions(currentInvoice.termsAndConditions || '');
      setTaxRate(currentInvoice.taxRate || '');
      setDiscountRate(currentInvoice.discountRate || '');
      setItems(currentInvoice.items || [{ id: "0", description: '', price: '0.00', quantity: 1 }]);
      setLogo(currentInvoice.logo || '');
    } else {
      // Reset to new invoice defaults via context (handles numbering and flag)
      createNewInvoice();
      // Fixed: Explicitly set the next invoice number for new invoices
      setInvoiceNumber((parseInt(lastInvoiceNumber) + 1).toString());
      setCurrency('$');
      setDateOfIssue('');
      setInvoiceDate('');
      setBillTo('');
      setBillToEmail('');
      setBillToAddress('');
      setBillToDocumentId('');  // NEW
      setBillFrom('');
      setBillFromEmail('');
      setBillFromAddress('');
      setBillFromDocumentId('');  // NEW
      setNotes('');
      setTermsAndConditions('');
      setTaxRate('');
      setDiscountRate('');
      setItems([{ id: "0", description: '', price: '0.00', quantity: 1 }]);
      setLogo('');
    }
  }, [currentInvoice, createNewInvoice, lastInvoiceNumber]);  // Added lastInvoiceNumber for reactivity

  // Handlers (extracted and adapted) - unchanged
  const handleRowDel = (itemToDelete) => {
    setItems(items.filter(item => item !== itemToDelete));
  };

  const handleAddEvent = () => {
    const id = (+new Date() + Math.floor(Math.random() * 999999)).toString(36);
    setItems([...items, { id, description: '', price: '1.00', quantity: 1 }]);
  };

  const onItemizedItemEdit = (evt, itemId) => {
    const { name, value } = evt.target;
    setItems(items.map(item => item.id === itemId ? { ...item, [name]: value } : item));
  };

  // Updated: Added Document ID handlers
  const editField = (event) => {
    const { name, value } = event.target;
    switch (name) {
      case 'billTo': setBillTo(value); break;
      case 'billToEmail': setBillToEmail(value); break;
      case 'billToAddress': setBillToAddress(value); break;
      case 'billToDocumentId': setBillToDocumentId(value); break;  // NEW
      case 'billFrom': setBillFrom(value); break;
      case 'billFromEmail': setBillFromEmail(value); break;
      case 'billFromAddress': setBillFromAddress(value); break;
      case 'billFromDocumentId': setBillFromDocumentId(value); break;  // NEW
      case 'notes': setNotes(value); break;
      case 'termsAndConditions': setTermsAndConditions(value); break;
      case 'dateOfIssue': setDateOfIssue(value); break;
      case 'invoiceDate': setInvoiceDate(value); break;
      case 'invoiceNumber': setInvoiceNumber(value); break;
      case 'taxRate': setTaxRate(value); break;
      case 'discountRate': setDiscountRate(value); break;
      default: break;
    }
  };

  const onCurrencyChange = (selectedOption) => {
    setCurrency(selectedOption.currency);
  };

  const openModal = (event) => {
    event.preventDefault();
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  // Updated: Return new Document ID fields
  return {
    // State
    isOpen, currency, invoiceNumber, dateOfIssue, invoiceDate,
    billTo, billToEmail, billToAddress, billToDocumentId,  // NEW
    billFrom, billFromEmail, billFromAddress, billFromDocumentId,  // NEW
    notes, termsAndConditions, taxRate, discountRate, items, logo,
    // Handlers
    handleRowDel, handleAddEvent, onItemizedItemEdit, editField, onCurrencyChange, openModal, closeModal,
    // Setters for calculations hook
    setItems, setTaxRate, setDiscountRate, setLogo
  };
};

export default useInvoice;